import React, { useState, useCallback, useRef, useEffect } from "react";
import {
    format,
    addMonths,
    subMonths,
    startOfMonth,
    startOfQuarter,
    subQuarters,
    isSameDay,
} from "date-fns";
import EventIcon from "@mui/icons-material/Event";
import { Calendar } from "./Calendar";
import "./DateRangePicker.scss";
import { useResponsiveStyles } from "../../../styles/useResponsiveStyles";
import { useGetYearConfigurationQuery } from "../../../redux/service/modules/admin";
import moment from "moment";

const parseDate = (dateValue) => {
    if (!dateValue) return null;

    if (dateValue instanceof Date) return dateValue;

    if (typeof dateValue === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(dateValue)) {
        return moment(dateValue, "DD-MM-YYYY").toDate();
    }

    return moment(dateValue).toDate();
};

const DATEPICKER_CLOSE_EVENT = "datepicker-close";

const getYearStart = (date, yearType) => {
    const currentYear = date.getFullYear();
    if (yearType === 'financial') {
        const currentMonth = date.getMonth();
        return new Date(currentMonth < 3 ? currentYear - 1 : currentYear, 3, 1);
    } else {
        return new Date(currentYear, 0, 1);
    }
};

const determineDatePreset = (startDate, endDate, yearType = 'calendar') => {
    if (!startDate || !endDate) return "year";

    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (!isSameDay(end, now)) return null;

    if (isSameDay(start, startOfMonth(now))) {
        return "month";
    }

    if (isSameDay(start, startOfQuarter(now))) {
        return "quarter";
    }

    const calendarYearStart = new Date(now.getFullYear(), 0, 1);
    const currentMonth = now.getMonth();
    const financialYearStart = new Date(
        currentMonth < 3 ? now.getFullYear() - 1 : now.getFullYear(),
        3,
        1
    );

    if (yearType === 'financial' && isSameDay(start, financialYearStart)) {
        return "year";
    } else if (yearType === 'calendar' && isSameDay(start, calendarYearStart)) {
        return "year";
    }

    const last4QuartersStart = subQuarters(startOfQuarter(now), 3);
    if (isSameDay(start, last4QuartersStart)) {
        return "last4quarters";
    }

    const last8QuartersStart = subQuarters(startOfQuarter(now), 7);
    if (isSameDay(start, last8QuartersStart)) {
        return "last8quarters";
    }

    return null;
};

export const DateRangePicker = ({
    onDateRangeChange,
    borderColor,
    dateRange,
    customStyles,
    customPopoverStyle,
    range = true,
    disableDateRangeSelection = false,
}) => {
    const { data: yearConfig } = useGetYearConfigurationQuery();
    const yearType = yearConfig?.year_type || 'calendar';
    const [isOpen, setIsOpen] = useState(false);
    const { getFontSize } = useResponsiveStyles();

    const [leftMonth, setLeftMonth] = useState(
        dateRange?.startDate ? parseDate(dateRange.startDate) : new Date()
    );
    const [rightMonth, setRightMonth] = useState(
        dateRange?.endDate ? parseDate(dateRange.endDate) : new Date()
    );
    const [startDate, setStartDate] = useState(
        dateRange?.startDate ? parseDate(dateRange.startDate) : null
    );
    const [endDate, setEndDate] = useState(
        dateRange?.endDate ? parseDate(dateRange.endDate) : null
    );
    const [hoverDate, setHoverDate] = useState(null);
    const [selectedPreset, setSelectedPreset] = useState(() =>
        dateRange?.startDate && dateRange?.endDate
            ? determineDatePreset(
                parseDate(dateRange.startDate),
                parseDate(dateRange.endDate),
                yearType
            )
            : "year"
    );

    const containerRef = useRef(null);

    useEffect(() => {
        if (dateRange?.startDate && dateRange?.endDate) {
            const start = parseDate(dateRange.startDate);
            const end = parseDate(dateRange.endDate);
            setStartDate(start);
            setEndDate(end);
            setLeftMonth(start);
            setRightMonth(end);
            setSelectedPreset(determineDatePreset(start, end, yearType));
        }
    }, [dateRange, yearType]);

    const handlePresetClick = useCallback(
        (preset) => {
            const now = new Date();
            let start;

            switch (preset) {
                case "month":
                    start = startOfMonth(now);
                    break;
                case "quarter":
                    start = startOfQuarter(now);
                    break;
                case "year":
                    if (yearType === 'financial') {
                        const currentMonth = now.getMonth();
                        start = new Date(
                            currentMonth < 3 ? now.getFullYear() - 1 : now.getFullYear(),
                            3,
                            1
                        );
                    } else {
                        start = new Date(now.getFullYear(), 0, 1);
                    }
                    break;
                case "last4quarters":
                    start = subQuarters(startOfQuarter(now), 3);
                    break;
                case "last8quarters":
                    start = subQuarters(startOfQuarter(now), 7);
                    break;
                default:
                    return;
            }

            setStartDate(start);
            setEndDate(now);
            setLeftMonth(start);
            setRightMonth(now);
            setSelectedPreset(preset);
            onDateRangeChange(
                moment(start).format("DD-MM-YYYY"),
                moment(now).format("DD-MM-YYYY")
            );
            setIsOpen(false);
        },
        [yearType, onDateRangeChange]
    );

    const handleDateClick = useCallback(
        (date, isSecondCalendar = false) => {
            if (!moment(date).isValid()) return;

            if (!range) {
                const formattedDate = moment(date).format("DD-MM-YYYY");
                setEndDate(date);
                onDateRangeChange(formattedDate, formattedDate);
                setIsOpen(false);
                return;
            }

            if (!startDate) {
                setStartDate(date);
                setEndDate(null);
                setSelectedPreset(null);
                if (!isSecondCalendar) {
                    setRightMonth(addMonths(date, 1));
                }
            } else if (!endDate) {
                if (date < startDate) {
                    setEndDate(startDate);
                    setStartDate(date);
                    setSelectedPreset(null);
                    if (!isSecondCalendar) {
                        setRightMonth(addMonths(date, 1));
                    }
                } else {
                    setEndDate(date);
                    setSelectedPreset(null);
                }
            } else {
                setStartDate(date);
                setEndDate(null);
                setSelectedPreset(null);
                if (!isSecondCalendar) {
                    setRightMonth(addMonths(date, 1));
                }
            }
        },
        [startDate, endDate, range, onDateRangeChange]
    );

    const handleLeftPrevMonth = useCallback((newDate) => {
        if (newDate) {
            setLeftMonth(newDate);
        } else {
            setLeftMonth((prev) => subMonths(prev, 1));
        }
    }, []);

    const handleLeftNextMonth = useCallback(
        (newDate) => {
            if (newDate) {
                setLeftMonth(newDate);
            } else {
                const newLeftMonth = addMonths(leftMonth, 1);
                setLeftMonth(newLeftMonth);
                setRightMonth(addMonths(newLeftMonth, 1));
            }
        },
        [leftMonth]
    );

    const handleRightPrevMonth = useCallback(
        (newDate) => {
            if (newDate) {
                setRightMonth(newDate);
            } else {
                const newRightMonth = subMonths(rightMonth, 1);
                if (newRightMonth > leftMonth) {
                    setRightMonth(newRightMonth);
                }
            }
        },
        [leftMonth, rightMonth]
    );

    const handleRightNextMonth = useCallback((newDate) => {
        if (newDate) {
            setRightMonth(newDate);
        } else {
            setRightMonth((prev) => addMonths(prev, 1));
        }
    }, []);

    const handleApply = useCallback(() => {
        if (startDate && endDate) {
            onDateRangeChange(
                moment(startDate).format("DD-MM-YYYY"),
                moment(endDate).format("DD-MM-YYYY")
            );
            setIsOpen(false);
        }
    }, [startDate, endDate, onDateRangeChange]);

    const handleCancel = useCallback(() => {
        if (dateRange?.startDate && dateRange?.endDate) {
            const start = parseDate(dateRange.startDate);
            const end = parseDate(dateRange.endDate);
            setStartDate(start);
            setEndDate(end);
            setLeftMonth(start);
            setRightMonth(end);
        }
        setIsOpen(false);
    }, [dateRange]);

    const calculatePopupPosition = useCallback(() => {
        if (!containerRef.current) return {};

        const containerRect = containerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        const popupWidth = range ? 720 : 360;
        const popupHeight = 480;

        const spaceBelow = viewportHeight - containerRect.bottom;
        const spaceAbove = containerRect.top;
        const spaceRight = viewportWidth - containerRect.left;
        const spaceLeft = containerRect.right;

        let position = {};

        if (spaceRight >= popupWidth) {
            position.left = 0;
        } else if (spaceLeft >= popupWidth) {
            position.right = 0;
        } else {
            position.left = "50%";
            position.transform = "translateX(-50%)";
        }

        if (spaceBelow >= popupHeight) {
            position.top = "calc(100% + 8px)";
        } else if (spaceAbove >= popupHeight) {
            position.bottom = "calc(100% + 8px)";
        }
        position = {
            ...position,
            ...customPopoverStyle,
        };
        return position;
    }, [range, customPopoverStyle]);

    const handleTriggerClick = useCallback(() => {
        if (!isOpen) {
            document.dispatchEvent(
                new CustomEvent(DATEPICKER_CLOSE_EVENT, {
                    detail: { id: containerRef.current.id },
                })
            );
        }
        setIsOpen(!isOpen);
    }, [isOpen]);

    const handleClickOutside = useCallback((event) => {
        if (
            containerRef.current &&
            !containerRef.current.contains(event.target)
        ) {
            setIsOpen(false);
        }
    }, []);

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [handleClickOutside]);

    const getDisplayText = () => {
        if (!startDate || !moment(startDate).isValid()) return "Select Date";
        if (range && (!endDate || !moment(endDate).isValid())) return "Select Date Range";

        const presetDisplayNames = {
            month: "Month to Date",
            quarter: "Quarter to Date",
            year: "Year to Date",
            last4quarters: "Last 4 Quarters",
            last8quarters: "Last 8 Quarters",
        };

        if (selectedPreset) {
            return presetDisplayNames[selectedPreset];
        }

        try {
            return range && endDate
                ? `${format(startDate, "dd MMM yy")} - ${format(endDate, "dd MMM yy")}`
                : format(startDate, "dd MMM yy");
        } catch (error) {
            console.error("Date formatting error:", error);
            return "Select Date";
        }
    };

    return (
        <div
            className={`date-range-picker ${
                disableDateRangeSelection ? "disabled" : ""
            }`}
            ref={containerRef}
            id={`datepicker-${Math.random().toString(36).substr(2, 9)}`}
            style={customStyles}
        >
            <button
                onClick={() =>
                    !disableDateRangeSelection && handleTriggerClick()
                }
                className="date-range-trigger"
                style={{ borderColor: borderColor }}
            >
                <EventIcon style={{ fontSize: "1rem" }} />
                <span style={getFontSize("body1")}>{getDisplayText()}</span>
            </button>

            {isOpen && (
                <div
                    className={`date-range-popup ${
                        !range ? "single-date-popup" : ""
                    }`}
                    style={calculatePopupPosition()}
                >
                    <div className="date-range-content">
                        {range && (
                            <div className="preset-options xl:p-2 2xl:p-4 3xl:p-4">
                                <button
                                    onClick={() => handlePresetClick("month")}
                                >
                                    Month to Date
                                </button>
                                <button
                                    onClick={() => handlePresetClick("quarter")}
                                >
                                    Quarter to Date
                                </button>
                                <button
                                    onClick={() => handlePresetClick("year")}
                                >
                                    Year to Date
                                </button>
                                <button
                                    onClick={() =>
                                        handlePresetClick("last4quarters")
                                    }
                                >
                                    Last 4 Quarters
                                </button>
                                <button
                                    onClick={() =>
                                        handlePresetClick("last8quarters")
                                    }
                                >
                                    Last 8 Quarters
                                </button>
                            </div>
                        )}

                        <div className="calendars-container xl:p-2 2xl:p-4 3xl:p-4">
                            <div className="date-labels flex xl:mb-0 mb-2 2xl:mb-2 3xl:mb-4">
                                <div className="flex-1">
                                    <div className="flex flex-col">
                                        <span className="xl:text-xs 2xl:text-sm 3xl:text-base text-sm text-gray-500 mb-1">
                                            {range ? "From" : "Date"}
                                        </span>
                                        <span className="xl:text-xs 2xl:text-sm 3xl:text-base text-sm font-medium text-black">
                                            {startDate
                                                ? format(
                                                      startDate,
                                                      "yyyy/MM/dd"
                                                  )
                                                : ""}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex-1 ml-10">
                                    {range && (
                                        <div className="flex flex-col">
                                            <span className="xl:text-xs 2xl:text-sm 3xl:text-base text-sm text-gray-500 mb-1">
                                                To
                                            </span>
                                            <span className="xl:text-xs 2xl:text-sm 3xl:text-base text-sm font-medium text-black">
                                                {endDate
                                                    ? format(
                                                          endDate,
                                                          "yyyy/MM/dd"
                                                      )
                                                    : ""}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="calendars">
                                <Calendar
                                    date={leftMonth}
                                    startDate={startDate}
                                    endDate={endDate}
                                    hoverDate={range ? hoverDate : null}
                                    onDateClick={(date) =>
                                        handleDateClick(date, false)
                                    }
                                    onDateHover={setHoverDate}
                                    onPrevMonth={handleLeftPrevMonth}
                                    onNextMonth={handleLeftNextMonth}
                                />
                                {range && (
                                    <Calendar
                                        date={rightMonth}
                                        startDate={startDate}
                                        endDate={endDate}
                                        hoverDate={hoverDate}
                                        onDateClick={(date) =>
                                            handleDateClick(date, true)
                                        }
                                        onDateHover={setHoverDate}
                                        onPrevMonth={handleRightPrevMonth}
                                        onNextMonth={handleRightNextMonth}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="action-buttons xl:px-4 2xl:px-8 3xl:px-8 xl:gap-4 2xl:gap-8 3xl:gap-8 xl:py-2 2xl:py-4 3xl:py-4">
                        <button
                            onClick={handleCancel}
                            className="cancel-button font-semibold xl:text-sm 2xl:text-base 3xl:text-lg text-sm xl:px-2 2xl:px-2 3xl:px-2 xl:py-1 2xl:py-1 3xl:py-4 "
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleApply}
                            className="apply-button font-semibold xl:text-sm 2xl:text-base 3xl:text-lg text-sm xl:px-2 2xl:px-2 3xl:px-2 xl:py-1 2xl:py-1 3xl:py-4 "
                            disabled={
                                dateRange ? !startDate || !endDate : !startDate
                            }
                        >
                            Apply
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
